// frontend/src/services/mediaService.ts
import apiClient from './apiClient';
import { MediaAsset } from '../types/Portfolio';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

interface MediaUploadResponse {
  assets: MediaAsset[];
  total: number;
  page: number;
  totalPages: number;
}

interface MediaFilters {
  category?: string;
  tags?: string;
  limit?: number;
  page?: number;
}

export const mediaService = {
  // Upload media file
  upload: async (
    file: File,
    metadata?: {
      category?: string;
      altText?: string;
      caption?: string;
      tags?: string[];
    }
  ): Promise<MediaAsset> => {
    const formData = new FormData();
    formData.append('receiptPhoto', file);
    
    if (metadata?.category) formData.append('category', metadata.category);
    if (metadata?.altText) formData.append('altText', metadata.altText);
    if (metadata?.caption) formData.append('caption', metadata.caption);
    if (metadata?.tags) formData.append('tags', metadata.tags.join(','));

    const token = localStorage.getItem('token');
    
    const response = await axios.post<MediaAsset>(
      `${API_BASE_URL}/api/admin/media`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );
    
    // Transform _id to id
    return {
      ...response.data,
      id: (response.data as any)._id
    };
  },

  // Get all media assets with filters
  getAll: async (filters?: MediaFilters): Promise<MediaUploadResponse> => {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.tags) params.append('tags', filters.tags);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.page) params.append('page', filters.page.toString());

    const queryString = params.toString();
    const url = `/api/admin/media${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiClient.get<MediaUploadResponse>(url);
    
    // Transform _id to id for all assets
    response.assets = response.assets.map(asset => ({
      ...asset,
      id: (asset as any)._id
    }));
    
    return response;
  },

  // Get single media asset by ID
  getById: async (id: string): Promise<MediaAsset> => {
    const response = await apiClient.get<MediaAsset>(`/api/admin/media/${id}`);
    return {
      ...response,
      id: (response as any)._id
    };
  },

  // Update media asset metadata
  update: async (
    id: string,
    data: {
      category?: string;
      altText?: string;
      caption?: string;
      tags?: string[];
    }
  ): Promise<MediaAsset> => {
    return apiClient.put<MediaAsset>(`/api/admin/media/${id}`, data);
  },

  // Delete media asset
  delete: async (id: string): Promise<{ message: string }> => {
    return apiClient.delete<{ message: string }>(`/api/admin/media/${id}`);
  },
};

export default mediaService;